"use client";

import React, { useState } from "react";
import { newsletterSchema } from "@/lib/validations";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");

    // Validate email client-side
    const result = newsletterSchema.safeParse({ email });
    if (!result.success) {
      setErrorMsg(result.error.issues[0].message);
      setStatus("error");
      return;
    }

    try {
      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formType: "newsletter",
          email,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus("success");
        setEmail("");
      } else {
        throw new Error(data.error || "Failed to subscribe");
      }
    } catch (err) {
      console.error("Newsletter subscription error:", err);
      setErrorMsg(err instanceof Error ? err.message : "Failed to subscribe. Please try again.");
      setStatus("error");
    }
  };

  return (
    <section className="mb-section-gap px-margin-mobile md:px-gutter max-w-container-max mx-auto">
      <div className="bg-primary p-6 sm:p-12 md:p-20 rounded-xl relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-12 shadow-lg">
        <div className="relative z-10 max-w-lg">
          <h2 className="font-headline-lg-mobile text-headline-lg-mobile md:text-headline-lg md:font-headline-lg text-on-primary mb-4 font-bold text-white">Stay at the Cutting Edge</h2>
          <p className="text-white/80 font-body-lg text-body-lg leading-relaxed">Get bi-weekly technical deep dives on AI architectures delivered directly to your inbox.</p>
        </div>
        <div className="relative z-10 w-full lg:w-auto flex flex-col gap-2">
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
            <input 
              className={`px-5 py-3 sm:px-6 sm:py-3.5 rounded-xl bg-white/10 border-2 ${
                status === "error" ? "border-red-400 focus:border-red-400" : "border-white/20 focus:border-white"
              } text-white placeholder:text-white/60 focus:outline-none w-full sm:min-w-[300px] text-sm transition-colors`} 
              placeholder="Enter your work email" 
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              disabled={status === "submitting" || status === "success"}
              aria-label="Work email address"
            />
            <button 
              type="submit" 
              disabled={status === "submitting" || status === "success"}
              className="bg-white text-primary px-6 py-3 sm:px-8 sm:py-3.5 rounded-xl font-bold hover:bg-primary-light hover:text-primary transition-all shadow-md hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed min-w-[130px] text-sm sm:text-base"
            >
              {status === "submitting" ? "Subscribing..." : status === "success" ? "Subscribed!" : "Subscribe"}
            </button>
          </form>
          {status === "error" && (
            <p className="text-red-300 text-xs font-semibold animate-fadeIn">{errorMsg}</p>
          )}
          {status === "success" && (
            <p className="text-green-300 text-xs font-semibold animate-fadeIn">Thank you for subscribing!</p>
          )}
        </div>
        {/* Abstract Design Decor */}
        <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -left-20 -top-20 w-80 h-80 bg-black/10 rounded-full blur-3xl pointer-events-none"></div>
      </div>
    </section>
  );
}
